import api from './api';
import { MigrationHistoryResponse } from '../types';
import orgService from './orgService';

// Migration History API functions
export const migrationApi = {
  getMigrationHistory: async (page: number = 1, limit: number = 10): Promise<MigrationHistoryResponse> => {
    try {
      const response = await api.get(`/dev/api/migration/history?limit=${limit}&page=${page}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching migration history:', error);
      throw error;
    }
  },
  searchMigrationHistory: async (query: string, page: number = 1, limit: number = 10): Promise<MigrationHistoryResponse> => {
    try {
      const response = await api.get(`/dev/api/migration/search?query=${encodeURIComponent(query)}&limit=${limit}&page=${page}`);
      return response.data;
    } catch (error) {
      console.error('Error searching migration history:', error);
      throw error;
    }
  },

  // Migration Process API functions
  uploadFile: async (file: File): Promise<any> => {
    try {
      // Get organization_id from localStorage
      const organizationId = orgService.getOrganizationId();
      
      if (!organizationId) {
        throw new Error('Organization ID is missing. Please login again.');
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      // Append organization_id to the URL
      const response = await api.post(`/dev/file/upload/${organizationId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },

  loadToMongo: async (): Promise<any> => {
    try {
      // COMMENTED OUT: Body parameter removed (zip file upload removed)
      const response = await api.post('/dev/api/migration/load-data-to-MongoDB-from-Endevor');
      return response.data;
    } catch (error) {
      console.error('Error loading data to MongoDB:', error);
      throw error;
    }
  },

  transform: async (source: string, destination: string, migrationId: string, repoName: string, branchName: string): Promise<any> => {
    try {
      const response = await api.post(
        '/dev/api/migration/transform-or-migrate',
        {
          source,
          destination,
          operation: 'transform',
          repo_name: repoName,
          branch_name: branchName
        },
        {
          params: {
            migration_id: migrationId
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error transforming data:', error);
      throw error;
    }
  },

  migrate: async (source: string, destination: string, migrationId: string, repoName: string, branchName: string): Promise<{ data: string }> => {
    try {
      const response = await api.post(
        '/dev/api/migration/transform-or-migrate',
        {
          source,
          destination,
          operation: 'migrate',
          repo_name: repoName,
          branch_name: branchName
        },
        {
          params: {
            migration_id: migrationId
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error migrating data:', error);
      throw error;
    }
  },

  // Cleanup API function
  cleanup: async (): Promise<any> => {
    try {
      const response = await api.delete('/api/cleanup/clean');
      return response.data;
    } catch (error) {
      console.error('Error cleaning up:', error);
      throw error;
    }
  },

  // Get report analysis
  getReportAnalysis: async (reportId: string): Promise<any> => {
    try {
      const response = await api.get('/api/reports/data', {
        params: {
          id: reportId
        }
      });
      return response.data?.message ?? response.data;
    } catch (error) {
      console.error('Error fetching report analysis:', error);
      throw error;
    }
  },

  // Generate and download report
  generateReport: async (reportId: string): Promise<{ blob: Blob; contentType: string }> => {
    try {
      const response = await api.get(`/api/reports/generate/${reportId}`, {
        responseType: 'blob', // Handle file downloads
      });
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      return { blob: response.data, contentType };
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    }
  },

  // Retry failed migration
  retryMigration: async (migrationId: string): Promise<{ status_code: number; message: string; data: string }> => {
    try {
      const response = await api.get('/dev/api/migration/retry-migration', {
        params: {
          migration_id: migrationId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error retrying migration:', error);
      throw error;
    }
  },

  // Retry load DB - uses existing loadToMongo endpoint with migration_id parameter
  retryLoadDb: async (migrationId: string): Promise<any> => {
    try {
      const response = await api.post('/dev/api/migration/load-data-to-MongoDB-from-Endevor', {}, {
        params: {
          migration_id: migrationId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error retrying load DB:', error);
      throw error;
    }
  }
};

